import { ReactNode, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  LayoutDashboard,
  Package,
  Users,
  CreditCard,
  BarChart3,
  FileText,
  Eye,
  Receipt,
  Settings,
  HelpCircle,
  User,
  ChevronDown,
  LogOut,
  Activity,
  Building2,
  CreditCard as CreditCardIcon,
  LifeBuoy,
  FileCode,
  Globe,
} from 'lucide-react';

import { useAuth } from '@/hooks/useAuth';
import { useBrandLogo } from '@/hooks/useBrandLogo';
import { supportedLanguages } from '@/lang';
import { PortalShell, PortalShellNavItem } from '@/components/layouts/portal-shell';

interface ProviderLayoutProps {
  children: ReactNode;
}

export function ProviderLayout({ children }: ProviderLayoutProps) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, provider, logout } = useAuth();
  const brandLogo = useBrandLogo();
  const brandName = provider?.name ?? provider?.slug ?? t('navigation.providerAccount');

  const navigation: PortalShellNavItem[] = useMemo(() => {
    const billingLabel = provider?.name
      ? provider.name + ' ' + t('navigation.billingLabelSuffix')
      : t('navigation.florentinBilling');

    return [
      {
        label: t('navigation.dashboard'),
        href: '/provider/dashboard',
        icon: LayoutDashboard,
      },
      {
        label: t('navigation.apiProductsAutomations'),
        href: '/provider/products',
        icon: Package,
      },
      {
        label: t('navigation.customers'),
        href: '/provider/customers',
        icon: Users,
      },
      {
        label: t('navigation.customerBilling'),
        href: '/provider/billing',
        icon: CreditCard,
      },
      {
        label: t('navigation.analytics'),
        href: '/provider/analytics',
        icon: BarChart3,
      },
      {
        label: t('navigation.documentation'),
        href: '/provider/docs',
        icon: FileText,
      },
      {
        label: t('navigation.customerPreview'),
        href: '/provider/preview',
        icon: Eye,
      },
      {
        label: billingLabel,
        href: '/provider/florentin-billing',
        icon: Receipt,
      },
      {
        label: t('navigation.settings'),
        href: '/provider/settings',
        icon: Settings,
      },
      {
        label: t('navigation.helpSupport'),
        href: '/provider/help',
        icon: HelpCircle,
      },
    ];
  }, [provider?.name, t]);

  const changeLanguage = (language: string) => {
    i18n.changeLanguage(language);
  };

  const currentLanguage =
    supportedLanguages.find((lang) => lang.code === i18n.language) || supportedLanguages[0];

  const handleLogout = async () => {
    await logout();
  };

  const getUserInitials = () => {
    if (!user?.name) return 'U';
    const names = user.name.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    }
    return user.name.substring(0, 2).toUpperCase();
  };

  return (
    <PortalShell
      navigation={navigation}
      logoHref="/provider/dashboard"
      logo={
        brandLogo ? (
          <img
            src={brandLogo}
            alt={`${brandName} logo`}
            className="h-6 w-auto max-w-[140px] object-contain"
          />
        ) : (
          <span className="text-lg font-semibold tracking-tight text-foreground">
            {brandName}
          </span>
        )
      }
      sidebarFooter={
        <div className="rounded-lg bg-muted px-3 py-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium">{t('navigation.growthPlan')}</span>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {t('navigation.planDetails', { price: '79€', fee: '2.5%' })}
          </div>
        </div>
      }
      headerRight={
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative flex h-8 items-center gap-2 px-2">
              <Avatar className="h-8 w-8">
                <AvatarImage src={user?.avatar} alt={user?.name || 'User'} />
                <AvatarFallback>{getUserInitials()}</AvatarFallback>
              </Avatar>
              <span className="hidden text-sm font-medium md:block">
                {user?.name || provider?.name || t('navigation.providerAccount')}
              </span>
              <ChevronDown className="ml-1 hidden h-4 w-4 text-muted-foreground md:block" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <div className="flex items-center gap-3 px-2 py-2">
              <Avatar className="h-10 w-10">
                <AvatarImage src={user?.avatar} alt={user?.name || 'User'} />
                <AvatarFallback>{getUserInitials()}</AvatarFallback>
              </Avatar>
              <div className="flex flex-col">
                <span className="font-medium">{user?.name || t('navigation.providerAccount')}</span>
                <span className="text-xs text-muted-foreground">{user?.email || 'provider@example.com'}</span>
              </div>
            </div>
            <DropdownMenuSeparator />

            <DropdownMenuLabel className="text-xs text-muted-foreground">
              {t('navigation.organization')}
            </DropdownMenuLabel>
            <DropdownMenuItem className="gap-2">
              <Building2 className="h-4 w-4" />
              <span>{provider?.name || t('navigation.agencyOrganization')}</span>
              <Badge variant="secondary" className="ml-auto">
                {t('navigation.admin')}
              </Badge>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="gap-2"
              onClick={() => navigate('/provider/settings/organization')}
            >
              <Users className="h-4 w-4" />
              <span>{t('navigation.manageMembers')}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />

            <DropdownMenuLabel className="text-xs text-muted-foreground">
              {t('navigation.account')}
            </DropdownMenuLabel>
            <DropdownMenuItem
              className="gap-2"
              onClick={() => navigate('/provider/settings/profile')}
            >
              <User className="h-4 w-4" />
              <span>{t('navigation.profile')}</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="gap-2"
              onClick={() => navigate('/provider/florentin-billing')}
            >
              <CreditCardIcon className="h-4 w-4" />
              <span>{t('navigation.billingPlan')}</span>
              <Badge variant="secondary" className="ml-auto">
                {t('navigation.growthPlan')}
              </Badge>
            </DropdownMenuItem>
            <DropdownMenuSeparator />

            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="gap-2">
                <Globe className="h-4 w-4" />
                <span>{t('navigation.language')}</span>
                <span className="ml-auto text-xs">
                  {currentLanguage.flag} {currentLanguage.name}
                </span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuRadioGroup value={i18n.language} onValueChange={changeLanguage}>
                  {supportedLanguages.map((language) => (
                    <DropdownMenuRadioItem key={language.code} value={language.code} className="gap-2">
                      <span>{language.flag}</span>
                      <span>{language.name}</span>
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuSubContent>
            </DropdownMenuSub>

            <DropdownMenuItem className="gap-2" onClick={() => navigate('/provider/settings')}>
              <Settings className="h-4 w-4" />
              <span>{t('navigation.settings')}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />

            <DropdownMenuItem className="gap-2" onClick={() => navigate('/provider/help')}>
              <LifeBuoy className="h-4 w-4" />
              <span>{t('navigation.supportCenter')}</span>
            </DropdownMenuItem>
            <DropdownMenuItem className="gap-2" onClick={() => navigate('/provider/help/api-docs')}>
              <FileCode className="h-4 w-4" />
              <span>{t('navigation.apiDocumentation')}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />

            <DropdownMenuItem onClick={handleLogout} className="gap-2">
              <LogOut className="h-4 w-4" />
              <span>{t('navigation.logout')}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      }
    >
      {children}
    </PortalShell>
  );
}
